import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment, useCallback, useState } from 'react'
import { FileUploader, Icons, InformationModal, Resource } from 'utoniq-ui'

import { ConsolePreSaleTitleFragment } from '@/clients/utoniq-core/schema'
import { BottomSafeArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { useDownloadFile } from '@/lib/downloadFile'

export * from './fragments'

type ApplicantData = {
  fullName: string
  win: string
  itemTitle: string
  purchaseCount: string
  paymentMethod: string
  appliedAt: string
}

export type State = {
  artistId: string
  progress: number
  applicantsCsvUrl: string | null
  preSale: ConsolePreSaleTitleFragment | null
  csvData: ApplicantData[]
  submitting: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onDrop: FileUploadInfo['onDrop']
  onRemoveCsvFile: () => Promise<void>
  onUpdateLotteryResultFromCsv: () => Promise<void>
}

export const ConsoleLotteryCsvUploadTemplate: Template<State, Action> = ({
  state,
  onDrop,
  onRemoveCsvFile,
  onUpdateLotteryResultFromCsv,
}) => {
  const { routerPages, generatePath, router } = useRouter<{
    preSaleId: string
  }>()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleLotteryManagements, {
            preSaleId: router.query.preSaleId ?? '#',
          }),
          title: state.preSale?.title,
        }}
        customNavigation={
          state.csvData.length ? (
            <FixedBottomCell
              winCount={state.csvData.length}
              onUpdateLotteryResultFromCsv={onUpdateLotteryResultFromCsv}
              submitting={state.submitting}
            />
          ) : null
        }
      >
        <HeadingTextCell />
        <DownloadCsvCell applicantsCsvUrl={state.applicantsCsvUrl} />
        <UploadCsvCell
          csvData={state.csvData}
          csvUploadInfo={{ onDrop, progress: state.progress }}
          onRemoveCsvFile={onRemoveCsvFile}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const HeadingTextCell: Cell = () => {
  const { t } = useTranslation('consoleLotteryCsvUpload')
  return (
    <CellFrame sx={{ pt: 4 }}>
      <Typography variant="h3" pb={2}>
        {t('bulkRegistrationTitle', '当選者の一括登録')}
      </Typography>
      <Typography variant="body2">{t('bulkRegistrationDesc')}</Typography>
    </CellFrame>
  )
}

const DownloadCsvCell: Cell<{ applicantsCsvUrl: State['applicantsCsvUrl'] }> =
  ({ applicantsCsvUrl }) => {
    const { showMessage, showError } = useMessage()
    const { t } = useTranslation('consoleLotteryCsvUpload')
    const { downloadCsv } = useDownloadFile()

    return (
      <CellFrame>
        <Stack sx={{ pb: 2, pt: 5 }}>
          <Typography variant="h5" sx={{}}>
            {t('downloadCsvLabel')}
          </Typography>
        </Stack>
        <Divider />
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ pt: 4, pb: 5 }}
        >
          <Button
            variant="outlined"
            disabled={!applicantsCsvUrl}
            startIcon={<Icons.Download sx={{ color: 'grey.700' }} />}
            onClick={() => {
              downloadCsv(applicantsCsvUrl || '')
                .then(() => showMessage(t('downloadCsvComplete'), 'success'))
                .catch(showError)
            }}
          >
            {t('downloadButton', 'ダウンロード')}
          </Button>
        </Stack>
      </CellFrame>
    )
  }

type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

type UploadCsvCellProps = {
  csvUploadInfo: FileUploadInfo
  csvData: State['csvData']
  onRemoveCsvFile: Action['onRemoveCsvFile']
}
const UploadCsvCell: Cell<UploadCsvCellProps> = ({
  csvUploadInfo,
  csvData,
  onRemoveCsvFile,
}) => {
  const { t } = useTranslation('consoleLotteryCsvUpload')
  const { showMessage, showError } = useMessage()
  const [resource, setResource] = useState<Resource | undefined>(undefined)
  const [openTrashConfirm, setOpenTrashConfirm] = useState<boolean>(false)

  return (
    <CellFrame>
      <Stack sx={{ pb: 2, pt: 5 }}>
        <Typography variant="h5" sx={{}}>
          {t('uploadCsvLabel', '2.更新した応募者リストをアップロード')}
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ pt: 4 }}>
        {resource && (
          <Typography variant="body2">{t('uploadCsvNote')}</Typography>
        )}
        <FileUploader
          t={t}
          sx={{ p: 0 }}
          resource={resource}
          fileUploadInfo={{
            type: 'csv',
            onDrop() {
              return csvUploadInfo.onDrop().then(async resource => {
                if (!resource) {
                  return
                }
                setResource(resource)
                return resource
              })
            },
            progress: csvUploadInfo.progress || 0,
            description: t('csvUploaderDescription'),
            onConfirmClose() {
              setOpenTrashConfirm(true)
            },
            onClose() {},
          }}
          content={{ title: '' }}
        />
      </Stack>
      <PotentialWinnersCell csvData={csvData} />
      <InformationModal
        open={openTrashConfirm}
        onClose={() => setOpenTrashConfirm(false)}
        title={t('trashConfirmTitle')}
        content={t('trashConfirmDesc')}
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={async () => {
              await onRemoveCsvFile()
                .then(() => {
                  showMessage(t('removeSuccess'), 'success')
                  setResource(undefined)
                  setOpenTrashConfirm(false)
                })
                .catch(showError)
            }}
          >
            {t('delete', '削除する')}
          </Button>
        }
      />
    </CellFrame>
  )
}

const columns: {
  key: keyof ApplicantData
  width: number
}[] = [
  {
    key: 'fullName',
    width: 100,
  },
  {
    key: 'win',
    width: 60,
  },
  {
    key: 'itemTitle',
    width: 150,
  },
  {
    key: 'purchaseCount',
    width: 60,
  },
  {
    key: 'paymentMethod',
    width: 130,
  },
  {
    key: 'appliedAt',
    width: 150,
  },
]

type PotentialWinnersCellProps = {
  csvData: State['csvData']
}
const PotentialWinnersCell: Cell<PotentialWinnersCellProps> = ({ csvData }) => {
  const { t } = useTranslation('consoleLotteryCsvUpload')

  const renderText = useCallback(
    (key: keyof State['csvData'][number], value: string) => {
      if (key === 'win') {
        return t('choiceText', { number: value })
      }
      if (key === 'paymentMethod') {
        return t(value)
      }
      return value
    },
    [t]
  )

  if (!csvData.length) {
    return null
  }

  return (
    <CellFrame sx={{ pt: 2, pb: 6 }}>
      <Typography variant="h6" color="text.secondary" sx={{ py: 2 }}>
        {t('potentialWinner', { number: csvData.length })}
      </Typography>
      <TableContainer>
        <Table sx={{ width: 'auto', ovrerflowX: 'scroll' }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell sx={{ minWidth: col.width, py: 2, px: 0 }}>
                  <Typography
                    variant="subtitle1"
                    color="text.hint"
                    sx={{ display: 'block' }}
                  >
                    {t(col.key)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            loading={false}
            hasNextPage={false}
            onFetchMore={async () => {}}
            component={TableRow}
            colSpan={columns.length}
            nodes={csvData.map((data, index) => (
              <Fragment key={index}>
                {columns.map(col => (
                  <TableCell
                    key={col.key}
                    padding="none"
                    sx={{
                      py: 2,
                      pl: 0,
                      pr: 1,
                      width: col.width,
                    }}
                  >
                    <Stack flex={1} sx={{ minWidth: 0, width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{ width: col.width, ...styleSets.lineCamp(1) }}
                      >
                        {renderText(col.key, data[col.key])}
                      </Typography>
                    </Stack>
                  </TableCell>
                ))}
              </Fragment>
            ))}
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

type FixedBottomCellProps = {
  winCount: number
  submitting: boolean
  onUpdateLotteryResultFromCsv: Action['onUpdateLotteryResultFromCsv']
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  winCount,
  submitting,
  onUpdateLotteryResultFromCsv,
}) => {
  const { t } = useTranslation('consoleLotteryCsvUpload')
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          onClick={() => setOpenConfirm(true)}
        >
          {t('confirm')}
        </Button>
      </CellFrame>
      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('confirmTitle')}
        content={
          <Box sx={{ pb: 7 }}>
            <Typography variant="subtitle1" align="left">
              {t('confirmDesc')}
            </Typography>
            <Typography variant="body2" align="left">
              {t('peopleWin', { number: winCount })}
            </Typography>
          </Box>
        }
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={async () => {
              onUpdateLotteryResultFromCsv().finally(() => {
                setOpenConfirm(false)
              })
            }}
            loading={submitting}
          >
            {t('confirm', '確定する')}
          </Button>
        }
      />
    </BottomSafeArea>
  )
}
